import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './StreamControl.css';

/**
 * Owner-only streaming control for a live match.
 *
 * Single stream slot: only one match can be streamed at a time. This panel
 * lets the owner pick which match to stream when several run simultaneously,
 * which the auto-start flow cannot disambiguate. Renders nothing unless the
 * caller is the guild owner.
 *
 * Backend contract (all owner-gated):
 *   GET  /api/admin/streaming/status -> { enabled, auto, configured, target, active, match_id }
 *   POST /api/admin/streaming/start  { match_id } -> status (409 if another match active / streaming off)
 *   POST /api/admin/streaming/stop   -> { stopped_match_id, ...status }
 *
 * @param {object} props
 * @param {number|string} props.matchId - The match this control acts on.
 * @param {boolean} props.isOwner - Whether the current user is the guild owner.
 */
const StreamControl = ({ matchId, isOwner }) => {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/streaming/status`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        // 403 = not owner; just hide the panel silently.
        setStatus(null);
        return;
      }
      setStatus(await res.json());
      setError(null);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!isOwner) {
      setLoading(false);
      return;
    }
    fetchStatus();
    // Poll so the panel reflects streams started elsewhere (Discord/auto).
    const interval = setInterval(fetchStatus, 15000);
    return () => clearInterval(interval);
  }, [isOwner, fetchStatus]);

  const post = useCallback(async (path, body) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/streaming/${path}`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || t('live.serverResponded', { status: res.status }));
      }
      setStatus(data);
    } catch (e) {
      setError(e.message || t('matchDetail.unknownError'));
    } finally {
      setBusy(false);
    }
  }, [getAuthHeaders, t]);

  const startThis = useCallback(() => post('start', { match_id: matchId }), [post, matchId]);
  const stopActive = useCallback(() => post('stop', null), [post]);

  if (!isOwner || loading || !status) return null;

  // Streaming master toggle is off → point owner at the Funktioner tab.
  if (!status.configured) {
    return (
      <div className="stream-control stream-control--off">
        <span className="stream-control-label">🎥 {t('live.streamingOff')}</span>
        <span className="stream-control-hint">{t('live.enableInAdmin')}</span>
      </div>
    );
  }

  const activeId = status.match_id;
  const isThisMatch = activeId != null && String(activeId) === String(matchId);
  const otherActive = activeId != null && !isThisMatch;

  return (
    <div className={`stream-control${isThisMatch ? ' stream-control--live' : ''}`}>
      <span className="stream-control-label">
        {isThisMatch ? `🔴 ${t('live.streamingThis')}` : `🎥 ${t('live.streaming')}`}
      </span>

      {otherActive && (
        <span className="stream-control-hint">
          {t('live.otherStreaming', { id: activeId })}
        </span>
      )}

      <div className="stream-control-actions">
        {isThisMatch ? (
          <button
            type="button"
            className="stream-control-btn stream-control-btn--stop"
            onClick={stopActive}
            disabled={busy}
          >
            {busy ? t('live.stopping') : t('live.stopStream')}
          </button>
        ) : otherActive ? (
          <button
            type="button"
            className="stream-control-btn stream-control-btn--stop"
            onClick={stopActive}
            disabled={busy}
          >
            {busy ? t('live.stopping') : t('live.stopMatch', { id: activeId })}
          </button>
        ) : (
          <button
            type="button"
            className="stream-control-btn stream-control-btn--start"
            onClick={startThis}
            disabled={busy}
          >
            {busy ? t('live.starting') : t('live.streamThis')}
          </button>
        )}
      </div>

      {error && <span className="stream-control-error">{error}</span>}
    </div>
  );
};

export default StreamControl;
